import React, { useEffect, useMemo, useState } from 'react';
import Box from '@components/Atoms/Box';
import Select from '@components/Atoms/Select';
import { useSearchParams, useUpdateQueryParams } from '@utils/hooks/useSearchParams';
import {
  Chip,
  Autocomplete,
  TextField,
  Link,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useAvailableCurriculums } from '@reducers/curriculums/hooks';
import { useGetCurriculumSubjectUnits } from 'chalktalk-react/hooks/useGetCurriculumSubjectUnits';
import ShowIf from '@components/Atoms/ShowIf/ShowIf';

const ADMIN_URL = process.env.REACT_APP_ADMIN_URL;

const ContentSelector = ({ onChange }: { onChange: (data: any) => void }) => {
  const searchParams = useSearchParams() as Record<string, number | number[]>;
  const updateQueryParams = useUpdateQueryParams();
  const { curriculums: availableCurriculums, isLoading: isLoadingCurriculums } =
    useAvailableCurriculums();

  const [selectedCurriculum, setSelectedCurriculum] = useState(undefined);
  const [selectedSubject, setSelectedSubject] = useState(undefined);
  const [selectedUnit, setSelectedUnit] = useState(undefined);
  const [selectedLesson, setSelectedLesson] = useState(undefined);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [activeSkills, setActiveSkills] = useState([]);
  const [isUsingLegacyPractice, setIsUsingLegacyPractice] = useState(false);

  const { units: availableUnits, isLoading: isLoadingUnits } =
    useGetCurriculumSubjectUnits(selectedCurriculum?.id, selectedSubject?.id);

  useEffect(() => {
    const { curriculum, subject, unit, lesson } = searchParams;
    setSelectedCurriculum(availableCurriculums?.find((c) => c.id === Number(curriculum)));
    if (!selectedCurriculum) {
      return;
    }
    setSelectedSubject(
      selectedCurriculum?.available_subjects?.find((s) => s.id === Number(subject)),
    );
    if (!selectedSubject) {
      return;
    }
    setSelectedUnit(availableUnits?.find((u) => u.id === Number(unit)));
    if (!selectedUnit) {
      return;
    }
    // set the selected lesson
    setSelectedLesson(selectedUnit?.lessons?.find((l) => l.id === Number(lesson)));
  }, [
    availableCurriculums,
    availableUnits,
    searchParams,
    selectedCurriculum,
    selectedLesson,
    selectedSubject,
    selectedUnit,
  ]);

  const sortSkills = (skills: any[]) =>
    skills.sort((a, b) => {
      // first sort by those with OGA or OLP in their name, then by their parent name
      const aOGA = a.name.includes('OGA') || a.name.includes('OLP');
      const bOGA = b.name.includes('OGA') || b.name.includes('OLP');
      if (aOGA && !bOGA) {
        return 1;
      }
      if (!aOGA && bOGA) {
        return -1;
      }
      return a.parent?.name.localeCompare(b.parent?.name);
    });

  // skill stuffs
  useEffect(() => {
    const haUnitPractice = selectedUnit?.meta?.lesson_practice;
    const unitLessonsCanPractice = selectedUnit?.lessons?.some((l) => l.can_practice);
    const hasLegacyPractice =
      haUnitPractice && (selectedLesson?.can_practice || unitLessonsCanPractice);
    setIsUsingLegacyPractice(hasLegacyPractice);

    const nodeType = hasLegacyPractice ? 'assessment_nodes' : 'skills';
    const uniqueSkillsMap = new Map<number, any>();
    selectedUnit?.lessons?.forEach((lesson) => {
      (lesson?.[nodeType] || []).forEach((skill) => {
        if (!uniqueSkillsMap.has(skill.id)) {
          uniqueSkillsMap.set(skill.id, skill);
        }
      });
    });

    let toActiveSkills = Array.from(uniqueSkillsMap.values());
    if (selectedLesson && selectedLesson?.[nodeType]) {
      const lessonSkillIds = selectedLesson?.[nodeType]?.map((node) => node.id);
      toActiveSkills = toActiveSkills.filter((s) => lessonSkillIds.includes(s.id));
    }
    setActiveSkills(sortSkills(toActiveSkills));
  }, [selectedLesson, selectedUnit?.lessons, selectedUnit?.meta?.lesson_practice]);

  useEffect(() => {
    const { skills } = searchParams;
    if (!skills) {
      setSelectedSkills([]);
      return;
    }

    if (!activeSkills || activeSkills.length === 0) {
      return;
    }

    const skillIds = (skills as unknown as string).split(',').map(Number);
    setSelectedSkills(activeSkills.filter((s) => skillIds.includes(s.id)));
  }, [activeSkills, searchParams, searchParams.skills]);

  useEffect(() => {
    onChange({
      curriculum: selectedCurriculum,
      subject: selectedSubject,
      unit: selectedUnit,
      lesson: selectedLesson,
      skills: selectedSkills,
      activeSkills,
    });
  }, [
    selectedCurriculum,
    selectedSubject,
    selectedUnit,
    selectedLesson,
    selectedSkills,
    activeSkills,
    onChange,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
      <Box>
        <Select
          id="content-curriculum-select"
          label="Curriculum"
          size="small"
          list={useMemo(
            () =>
              (availableCurriculums || []).map((c) => ({ value: c.id, label: c.name })),
            [availableCurriculums],
          )}
          showEmptyItem
          emptyItemLabel={isLoadingCurriculums ? 'Loading...' : 'None'}
          value={selectedCurriculum?.id}
          onChangeHandler={(value) => {
            updateQueryParams({
              curriculum: value as string | number | '',
              subject: '',
              unit: '',
              lesson: '',
              skills: '',
              nodeTabs: '',
            });
          }}
          disabled={isLoadingCurriculums || availableCurriculums?.length === 0}
          fullWidth
          displayEmpty
          sx={{ width: 200 }}
        />
      </Box>

      <Box>
        <Select
          id="content-subject-select"
          label="Subject"
          size="small"
          list={useMemo(
            () =>
              (selectedCurriculum?.available_subjects || []).map((s) => ({
                value: s.id,
                label: s.display_name,
              })),
            [selectedCurriculum],
          )}
          showEmptyItem
          emptyItemLabel={isLoadingCurriculums ? 'Loading...' : 'None'}
          value={selectedSubject?.id}
          onChangeHandler={(value) => {
            updateQueryParams({
              subject: value as string | number | '',
              unit: '',
              lesson: '',
              skills: '',
            });
          }}
          fullWidth
          displayEmpty
          sx={{ width: 200 }}
        />
      </Box>

      <Box>
        <Select
          id="content-unit-select"
          label="Unit"
          size="small"
          list={useMemo(
            () => (availableUnits || []).map((u) => ({ value: u.id, label: u.name })),
            [availableUnits],
          )}
          showEmptyItem
          emptyItemLabel={isLoadingUnits ? 'Loading...' : 'None'}
          value={selectedUnit?.id}
          onChangeHandler={(value) => {
            updateQueryParams({
              unit: value as string | number | '',
              lesson: '',
              skills: '',
            });
          }}
          fullWidth
          displayEmpty
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <span
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedUnit?.name || 'None'}
              </span>
              <ShowIf If={Boolean(selectedUnit?.id)}>
                <Link
                  href={`${ADMIN_URL}/admin/contentgroups/unit/${selectedUnit?.id}/change/`}
                  target="_blank"
                  sx={{ textDecoration: 'none', ml: 1, flexShrink: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  title="Open in Django Admin"
                >
                  🔗
                </Link>
              </ShowIf>
            </Box>
          )}
          sx={{ width: 250 }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Select
          id="content-lesson-select"
          label="Lesson"
          size="small"
          list={useMemo(
            () =>
              (selectedUnit?.lessons || []).map((l) => ({
                value: l.id,
                label: l.name,
              })),
            [selectedUnit],
          )}
          showEmptyItem
          emptyItemLabel={isLoadingUnits ? 'Loading...' : 'None'}
          value={selectedLesson?.id}
          onChangeHandler={(value) => {
            updateQueryParams({
              lesson: value as string | number | '',
              skills: '',
            });
          }}
          fullWidth
          displayEmpty
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <span
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedLesson?.name || 'None'}
              </span>
              <ShowIf If={Boolean(selectedLesson?.id)}>
                <Link
                  href={`${ADMIN_URL}/admin/contentgroups/lessongroup/${selectedLesson?.id}/change/`}
                  target="_blank"
                  sx={{ textDecoration: 'none', ml: 1, flexShrink: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  title="Open in Django Admin"
                >
                  🔗
                </Link>
              </ShowIf>
            </Box>
          )}
          sx={{ width: 250 }}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={activeSkills}
          groupBy={(option) => {
            const suffix =
              option.name.includes('OGA') || option.name.includes('OLP') ? ' (OLP)' : '';
            return `${option?.parent?.name || ''} ${suffix}`.trim();
          }}
          value={selectedSkills}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => option.id}
          onChange={(_, nextValues) => {
            const ids = (nextValues || []).map((v) => v.id);
            updateQueryParams({ skills: ids.length > 0 ? ids.join(',') : '' });
          }}
          renderTags={(value) => {
            if (!value || value.length === 0) {
              return 'None';
            }

            const first = value[0];
            const restCount = value.length - 1;

            return [
              <Chip key={String(first.id)} label={first.name} sx={{ maxWidth: 140 }} />,
              restCount > 0 ? (
                <Chip
                  key="skill-more"
                  label={`+${restCount}`}
                  sx={{
                    backgroundColor: '#E6F0FF',
                    color: '#1D4ED8',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    border: '1px solid rgba(29, 78, 216, 0.35)',
                  }}
                />
              ) : null,
            ].filter(Boolean);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={isUsingLegacyPractice ? 'Assessment Nodes' : 'Skills'}
              placeholder={
                isUsingLegacyPractice ? 'Select Assessment Nodes...' : 'Select Skills...'
              }
            />
          )}
          size="small"
          sx={{ width: 300 }}
        />
      </Box>
      <Box sx={{ mt: 1, ml: 1 }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(searchParams.keepTable)}
                onClick={(e) => {
                  updateQueryParams({ keepTable: e.target.checked ? '1' : '' });
                }}
              />
            }
            label="Keep Table"
          />
        </FormGroup>
      </Box>
    </Box>
  );
};

export default ContentSelector;
